"use client";

import { useEffect, useMemo, useState } from "react";

type ElliottRow = {
  symbol: string;
  date?: string;
  direction: string;
  stage: string;
  points: [number, number][];
  projections?: {
    // New Keys
    wave2_retrace_zones?: number[];
    potential_wave3_targets?: number[];
    wave3_targets?: number[];
    wave4_support?: number[];
    wave5_targets?: number[];
    
    // Legacy Keys (optional support)
    wave2?: number[];
    wave3?: number[];
    wave4?: number[];
    wave5?: number[];
  };
};

export default function ElliottScannerPage() {
  const [rows, setRows] = useState<ElliottRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [directionFilter, setDirectionFilter] = useState("all");
  const [openRow, setOpenRow] = useState<number | null>(null);
  const [latestPrices, setLatestPrices] = useState<Record<string, { close: number; date: string }>>({});

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        if (directionFilter !== "all") params.set("direction", directionFilter);
        const res = await fetch(`/api/outputs/elliott_impulse.json?${params.toString()}`);
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        const data = await res.json();
        setRows(data.results || []);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to load";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [directionFilter]);

  useEffect(() => {
    if (rows.length === 0) {
      setLatestPrices({});
      return;
    }
    const symbols = Array.from(new Set(rows.map((r) => r.symbol)));
    let cancelled = false;
    const fetchPrices = async () => {
      try {
        const response = await fetch("/api/prices/latest", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ symbols }),
        });
        if (!response.ok) return;
        const data: unknown = await response.json();
        const results = data && typeof data === "object" ? (data as { results?: unknown }).results : null;
        if (results && typeof results === "object") {
          const map: Record<string, { close: number; date: string }> = {};
          Object.values(results as Record<string, unknown>).forEach((row) => {
            if (!row || typeof row !== "object") return;
            const entry = row as { symbol?: string; close?: number; date?: string };
            if (!entry.symbol) return;
            map[entry.symbol] = {
              close: typeof entry.close === "number" ? entry.close : 0,
              date: typeof entry.date === "string" ? entry.date : "",
            };
          });
          if (!cancelled) setLatestPrices(map);
        }
      } catch {}
    };
    fetchPrices();
    return () => {
      cancelled = true;
    };
  }, [rows]);

  const subtitle = useMemo(
    () =>
      "In‑progress Elliott impulse (1‑2‑3‑4‑5) detection using swing points. Medium strictness, daily timeframe.",
    []
  );

  return (
    <main>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">Elliott Impulse</h1>
          <p className="page-subtitle">{subtitle}</p>
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
            <label style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>Direction</label>
            <select
              value={directionFilter}
              onChange={(e) => setDirectionFilter(e.target.value)}
              style={{
                padding: "0.45rem 0.7rem",
                borderRadius: "0.5rem",
                background: "rgba(15, 23, 42, 0.6)",
                color: "var(--color-text)",
                border: "1px solid rgba(148, 163, 184, 0.25)",
              }}
            >
              <option value="all">All</option>
              <option value="bullish">Bullish</option>
              <option value="bearish">Bearish</option>
            </select>
          </div>

          {loading && <div style={{ color: "var(--color-text-muted)" }}>Loading...</div>}
          {error && <div style={{ color: "#fca5a5" }}>{error}</div>}

          {!loading && !error && (
            <div style={{ overflowX: "auto" }}>
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Direction</th>
                    <th>Stage</th>
                    <th>Last Point</th>
                    <th>Current Price</th>
                    <th>Targets</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const lastPoint = row.points?.[row.points.length - 1];
                    const proj = row.projections || {};
                    const current = latestPrices[row.symbol]?.close;

                    // Helper to format a label + value list
                    const renderProj = (label: string, vals?: number[]) => {
                      if (!vals || vals.length === 0) return null;
                      return (
                        <div style={{ marginTop: "0.25rem" }}>
                          <span style={{ color: "var(--color-text-muted)", marginRight: "0.5rem" }}>{label}:</span>
                          <span style={{ color: "var(--color-text)" }}>{vals.map((v) => v.toFixed(2)).join(" / ")}</span>
                        </div>
                      );
                    };

                    return (
                      <tr
                        key={`${row.symbol}-${idx}`}
                        onClick={() => setOpenRow(openRow === idx ? null : idx)}
                        style={{ cursor: "pointer" }}
                      >
                        <td style={{ fontWeight: 600 }}>{row.symbol}</td>
                        <td style={{ color: row.direction === "Bullish" ? "var(--color-success)" : "var(--color-danger)" }}>{row.direction}</td>
                        <td>{row.stage}</td>
                        <td>{lastPoint ? lastPoint[1].toFixed(2) : "—"}</td>
                        <td>{typeof current === "number" ? current.toFixed(2) : "—"}</td>
                        <td>
                          {openRow === idx ? (
                            <div style={{ fontSize: "0.80rem" }}>
                               {renderProj("W2 Retrace Zones", proj.wave2_retrace_zones)}
                               {renderProj("Potential W3 Targets", proj.potential_wave3_targets)}
                               {renderProj("W3 Targets", proj.wave3_targets)}
                               {renderProj("W4 Support", proj.wave4_support)}
                               {renderProj("W5 Targets", proj.wave5_targets)}
                               
                               {/* Fallback for any legacy data */}
                               {renderProj("Wave 2", proj.wave2)}
                               {renderProj("Wave 3", proj.wave3 && !proj.wave3_targets ? proj.wave3 : undefined)}
                               {renderProj("Wave 4", proj.wave4 && !proj.wave4_support ? proj.wave4 : undefined)}
                               {renderProj("Wave 5", proj.wave5 && !proj.wave5_targets ? proj.wave5 : undefined)}
                            </div>
                          ) : (
                            <span style={{ color: "var(--color-primary)", fontSize: "0.85rem" }}>View Targets</span>
                          )}
                        </td>
                        <td style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>{row.date ?? "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
